import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Timer, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const QUICK = [10, 20, 30, 45, 60];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

/* "Running late — ETA N min" for the field portal. One tap from the late push (?eta=20) auto-sends; ?late=1 opens the picker.
   Shown only while this engineer hasn't arrived. `current` = my_attendance {eta_at, eta_time, eta_note}. */
export default function RunningLateButton({ entityType, entityId, current, onSaved, disabled = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState(20);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const autoFired = useRef(false);

  const send = async (mins, n = "") => {
    setSaving(true);
    try {
      const { data } = await API.post(`/in-house-portal/${entityType}/${entityId}/eta`, { eta_minutes: mins, note: n });
      toast.success(`ETA ${data.eta_time} sent — the office${data.notified ? "" : ""} and client have been told`);
      onSaved?.(data);
      setOpen(false);
      setNote("");
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send your ETA");
      return false;
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (autoFired.current) return;
    const params = new URLSearchParams(location.search);
    const eta = Number(params.get("eta"));
    if (eta >= 5 && eta <= 480) {
      autoFired.current = true;
      send(eta).finally(() => navigate(location.pathname, { replace: true }));
    } else if (params.get("late")) {
      autoFired.current = true;
      setOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const effective = custom ? Number(custom) : minutes;
  const valid = effective >= 5 && effective <= 480;

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={disabled || saving}
        className={current?.eta_at ? "border-amber-400 text-amber-800 dark:text-amber-300" : ""} data-testid="running-late-btn">
        <Timer className="h-4 w-4 mr-2" /> {current?.eta_at ? `ETA ${current.eta_time || fmtTime(current.eta_at)} · update` : "Running late?"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="running-late-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warning size={18} weight="fill" className="text-amber-500" /> Running late — send your ETA</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">The office, your company and the client will see your new arrival time straight away.</p>
          <div className="flex flex-wrap gap-2" data-testid="running-late-quick">
            {QUICK.map((m) => (
              <button key={m} type="button" onClick={() => { setMinutes(m); setCustom(""); }} data-testid={`running-late-quick-${m}`}
                className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors min-w-[64px] ${!custom && minutes === m ? "bg-amber-500 text-white border-amber-500" : "border-border hover:bg-muted"}`}>
                {m} min
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Or custom (minutes)</Label>
              <Input type="number" min={5} max={480} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 75" className="mt-1" data-testid="running-late-custom" />
            </div>
            <div className="text-xs text-muted-foreground self-end pb-2">
              {valid ? <>New ETA: <strong className="text-foreground" data-testid="running-late-preview">{fmtTime(new Date(Date.now() + effective * 60000).toISOString())}</strong></> : "5 min – 8 hours"}
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={200} placeholder="Stuck on the M25 / picking up parts…" className="mt-1" data-testid="running-late-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => send(effective, note)} disabled={saving || !valid} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="running-late-send">
              {saving ? "Sending…" : `Send ETA (${effective} min)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Amber banner for admin / supplier / client pages: the latest "running late" ETA while that engineer still hasn't arrived.
   `hideName` for the client portal (no engineer names). */
export function CrewEtaBanner({ job, hideName = false, className = "" }) {
  const eta = job?.crew_eta;
  if (!eta || ["completed", "cancelled"].includes(job.status)) return null;
  if (job.crew_attendance?.[eta.user_id]?.arrived_at) return null;
  const who = hideName ? "Your engineer" : eta.name || "Engineer";
  return (
    <div className={`border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 rounded-lg px-4 py-3 flex items-start gap-3 ${className}`} data-testid="crew-eta-banner">
      <Timer size={20} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-semibold text-amber-900 dark:text-amber-200" data-testid="crew-eta-title">{who} is running late — new arrival time {eta.eta_time || fmtTime(eta.eta_at)}</p>
        <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80">
          Sent {fmtTime(eta.set_at)}{eta.minutes ? ` · ${eta.minutes} min from then` : ""}{!hideName && eta.note ? ` · “${eta.note}”` : ""}
        </p>
      </div>
    </div>
  );
}
